import { BoothFeedbackCategory } from '@cibli/shared';
import { Micdrop, Speaker } from '@micdrop/web';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';

import { initSessionBuffer } from '@/lib/session-buffer';

import { useFeedbackAssistant, type FeedbackAudioKey } from './hooks/useFeedbackAssistant';
import { useFeedbackLocale } from './hooks/useFeedbackLocale';
import { initOfflineDb } from './lib/offlineDb';

interface FeedbackContextState {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  playAssistant: (key: FeedbackAudioKey, category?: BoothFeedbackCategory) => Promise<void>;
  stopAssistant: () => void;
  isPlaying: boolean;
}

const FeedbackContext = createContext<FeedbackContextState | undefined>(undefined);

export function FeedbackProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const { feedbackLocale } = useFeedbackLocale();
  const { play, stop, isPlaying } = useFeedbackAssistant(feedbackLocale);

  const open = useCallback(() => {
    // Pause and mute the interview conversation while feedback is open
    try {
      Micdrop.pause();
      Micdrop.mute();
    } catch {
      /* mic not active */
    }
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    // Stop all audio immediately on close
    if (Speaker.isPlaying) Speaker.stopAudio();
    stop();
    setIsOpen(false);
    // Resume and unmute the interview conversation
    try {
      Micdrop.unmute();
      Micdrop.resume();
    } catch {
      /* mic not active */
    }
  }, [stop]);

  useEffect(() => {
    initOfflineDb();
    initSessionBuffer();
  }, []);

  return (
    <FeedbackContext.Provider
      value={{ isOpen, open, close, playAssistant: play, stopAssistant: stop, isPlaying }}
    >
      {children}
    </FeedbackContext.Provider>
  );
}

export function useFeedbackContext(): FeedbackContextState {
  const context = useContext(FeedbackContext);
  if (context === undefined) {
    throw new Error('useFeedbackContext must be used within a FeedbackProvider');
  }
  return context;
}
